/**
 * Servicio centralizado para manejar permisos de usuarios en el módulo de working_hours
 */

export interface UserData {
  user_role?: {
    name: string;
    value: string;
  };
  user_type?: string;
  friendship_kind?: string;
  is_staff?: boolean;
}

export class PermissionsService {
  /**
   * Verifica si el usuario tiene permisos de administrador
   * (owner, admin, o customer/cliente)
   */
  static isAdmin(userData: UserData | null | undefined): boolean {
    if (!userData) return false;

    const roleValue = userData.user_role?.value;
    const userType = userData.user_type;
    const friendshipKind = userData.friendship_kind;

    // Verificar por user_role (fuente principal)
    if (roleValue === 'owner' || roleValue === 'admin' || roleValue === 'customer') {
      return true;
    }

    // Verificar por user_type (fuente alternativa)
    if (userType === 'owner' || userType === 'administrator' || userType === 'admin' || userType === 'customer') {
      return true;
    }

    // Verificar por friendship_kind (usado en working_hours)
    if (friendshipKind === 'propietario' || friendshipKind === 'admin' || friendshipKind === 'cliente') {
      return true;
    }

    return false;
  }

  /**
   * Verifica si el usuario es propietario (owner)
   */
  static isOwner(userData: UserData | null | undefined): boolean {
    if (!userData) return false;

    const roleValue = userData.user_role?.value;
    const userType = userData.user_type;
    const friendshipKind = userData.friendship_kind;

    return roleValue === 'owner' || userType === 'owner' || friendshipKind === 'propietario';
  }

  /**
   * Verifica si el usuario es administrador (admin)
   */
  static isAdministrator(userData: UserData | null | undefined): boolean {
    if (!userData) return false;

    const roleValue = userData.user_role?.value;
    const userType = userData.user_type;
    const friendshipKind = userData.friendship_kind;

    return roleValue === 'admin' || userType === 'admin' || userType === 'administrator' || friendshipKind === 'admin';
  }

  /**
   * Verifica si el usuario es cliente (customer)
   */
  static isCustomer(userData: UserData | null | undefined): boolean {
    if (!userData) return false;

    const roleValue = userData.user_role?.value;
    const userType = userData.user_type;
    const friendshipKind = userData.friendship_kind;

    return roleValue === 'customer' || userType === 'customer' || friendshipKind === 'cliente';
  }

  /**
   * Verifica si el usuario es empleado (employee)
   */
  static isEmployee(userData: UserData | null | undefined): boolean {
    if (!userData) return false;

    const roleValue = userData.user_role?.value;
    const userType = userData.user_type;
    const friendshipKind = userData.friendship_kind;

    return roleValue === 'employee' || userType === 'worker' || userType === 'employee' || friendshipKind === 'empleado';
  }

  /**
   * Verifica si el usuario es staff de Inmatic
   */
  static isStaff(userData: UserData | null | undefined): boolean {
    return userData?.is_staff === true;
  }

  /**
   * Verifica si el usuario puede ver el botón de administración
   * (owner, admin, customer o staff)
   */
  static canViewAdminButton(userData: UserData | null | undefined): boolean {
    if (!userData) return false;

    return (
      this.isAdmin(userData) ||
      this.isStaff(userData)
    );
  }

  /**
   * Verifica si el usuario puede gestionar usuarios
   * (owner, admin, o customer)
   */
  static canManageUsers(userData: UserData | null | undefined): boolean {
    return this.isAdmin(userData);
  }

  /**
   * Verifica si el usuario puede aprobar ausencias
   * (owner, admin, o customer)
   */
  static canApproveAbsences(userData: UserData | null | undefined): boolean {
    return this.isAdmin(userData);
  }

  /**
   * Obtiene el rol del usuario en formato legible
   */
  static getUserRoleDisplay(userData: UserData | null | undefined): string {
    if (!userData) return 'Desconocido';

    if (this.isOwner(userData)) return 'Propietario';
    if (this.isAdministrator(userData)) return 'Administrador';
    if (this.isCustomer(userData)) return 'Cliente';
    if (this.isEmployee(userData)) return 'Empleado';

    return userData.user_role?.name || 'Desconocido';
  }

  /**
   * Obtiene el valor del rol del usuario
   */
  static getUserRoleValue(userData: UserData | null | undefined): string | null {
    if (!userData) return null;

    return userData.user_role?.value || userData.user_type || null;
  }

  /**
   * Verifica si el usuario debe ir a /users-permissions
   * (owner o admin de la empresa)
   */
  static shouldNavigateToUsersPermissions(userData: UserData | null | undefined): boolean {
    if (!userData) return false;

    return this.isOwner(userData) || this.isAdministrator(userData);
  }
}
